import axios from 'axios'
import hash from 'object-hash'

import { commonChartOptions } from '../'
import { mergeDeepAll } from '../utils'
import {
  CompassAPIMultiSeriesChartData,
  convertCompassToHighchartsDataFormat,
  getCompassAPIChartData,
  getDataSources,
  getMultipleLineChartProps,
} from '../utils/compass'
import { formatSeriesNames } from '../utils/string'
import type { BaseContext } from './'
import { CommonChartConfig } from './common'

export namespace Transcripts {
  export namespace Inflation {
    const CONTEXT_ID = 'equity:transcripts:inflation'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'keywords_bz_us'
      context: typeof CONTEXT_ID
      title: string
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) =>
        getMultipleLineChartProps(
          Context,
          {
            chartTitle: config.title,
            tooltip: { xDateFormat: "Q%q'%y" },
            xAxis: {
              labels: { format: "{value:Q%q'%y}", rotation: -50 },
              tickPositions: data.data[0].data.map((point) =>
                new Date(point.date).getTime()
              ),
            },
            yAxis: [
              { id: 'primary', opposite: false, title: { text: '' } },
              { id: 'secondary', opposite: true, title: { text: '' } },
            ],
          },
          { yAxis: ['primary', 'secondary'] }
        )(config, formatSeriesNames(data)),
    }
  }

  export namespace InflationPerCountry {
    type PerCountryChartId = 'per_country'

    const CONTEXT_ID = 'equity:transcripts:inflation_per_country'
    const TRANSCRIPTS_CHARTS_PATH = '/api/charts/transcripts'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      context: typeof CONTEXT_ID
      chart: PerCountryChartId
      title: string
      country: string
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: TRANSCRIPTS_CHARTS_PATH + '/inflation/per-country',
        method: 'GET',
        params: { country: config.country },
      }).then((response) => response.data)

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const series = data.data.map((s, idx) => ({
        data: convertCompassToHighchartsDataFormat(s.data),
        id: `series-${idx}`,
        name: s.name,
        yAxis: 'default',
        ...(s.name === 'Total'
          ? {
              lineWidth: 0,
              marker: { enabled: true, radius: 2 },
              states: { hover: { lineWidthPlus: 0 } },
              stack: 'line',
              type: 'line',
            }
          : { stack: 'column', type: 'column', marker: { enabled: false } }),
      }))

      const tooltip = { xDateFormat: "Q%q'%y" }

      const plotOptions = { column: { minPointLength: 1, stacking: 'normal' } }

      const yAxis = [
        {
          endOnTick: false,
          id: 'default',
          labels: { format: '{value}' },
          offset: 0,
          opposite: false,
          startOnTick: false,
          title: { text: '' },
        },
      ]

      const xAxis = [
        {
          labels: { format: "{value:Q%q'%y}", rotation: -50 },
          tickPositions: data.data[0].data.map((point) =>
            new Date(point.date).getTime()
          ),
        },
      ]

      return {
        id: Context.getChartId(config),
        title: config.title,
        options: mergeDeepAll([
          commonChartOptions,
          getDataSources(data.data_sources),
          { plotOptions, series, tooltip, xAxis, yAxis },
        ]),
        stockTools: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chart}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }
}
